import { INTENSITY_BLOCK_COLORS, intensityBlockTextClass } from "@bcc/components/ui";
import { Badge } from "@bcc/components/ui/badge";
import { EmptyValue } from "@bcc/components/ui/empty-value";
import { cn } from "@bcc/lib/utils";
import { generateTestId } from "@client-shared";
import type { WebStatus } from "@shared/services/pim/types";
import { FLAVOUR_ZONES, type IntensityPosition, type IntensityZone, isIntensityPosition } from "@shared/types/intensity";
import type { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { ImageOff } from "lucide-react";
import { useState } from "react";
import { type AdminCatalogueTableRow, matchesBadgeSelection, matchesStatusSelection, toFilterSelection } from "./admin-catalogue-rows";
import { AdminAttributeCell, AdminWebStatusCell } from "./admin-editable-cell";
import { WEB_STATUS_BADGE_CLASSES, webStatusLabel } from "./admin-web-status";

export const ADMIN_PRODUCTS_COLUMN_IDS = {
  PRODUCT: "product",
  ITEM_NUMBER: "itemNumber",
  PARENT_ID: "parentId",
  ROASTER: "roaster",
  INTENSITY: "intensity",
  POSITION: "position",
  CHARACTER: "character",
  BADGES: "badges",
  WEB_STATUS: "webStatus",
} as const;

const COMPONENT = "AdminProductsColumns";

export const setAdminProductsCellTestId = (columnId: string, rowId: string): string => generateTestId(COMPONENT, `${columnId}-${rowId}`);

// Read-only thumbnail: the catalogue has no image actions, so it is not a button.
function CatalogueThumbnail({ imageUrl, name }: { readonly imageUrl?: string; readonly name: string }) {
  const [failed, setFailed] = useState(false);
  if (!imageUrl || failed) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted">
        <ImageOff className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }
  return (
    <img
      src={imageUrl}
      alt={name}
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-12 w-12 shrink-0 rounded-lg border object-cover"
    />
  );
}

// Inline background, as the SKU import scale does, so the colour ramp survives
// the production Tailwind purge. Shared by the Intensity pill and Position circle.
const intensityColorStyle = (position: IntensityPosition) => ({ backgroundColor: INTENSITY_BLOCK_COLORS[position] });

const textOrDash = (value: string | undefined) => (value ? <span className="text-sm">{value}</span> : <EmptyValue />);

// A zone has no position of its own, so its pill takes the colour of the zone's middle step.
const zoneColorPosition = (zone: IntensityZone): IntensityPosition => {
  const { min, max } = FLAVOUR_ZONES[zone];
  const middle = Math.round((min + max) / 2);
  return isIntensityPosition(middle) ? middle : min;
};

interface IntensityZoneBadgeProps {
  readonly t: TFunction;
  readonly rowId: string;
  readonly zone: IntensityZone | undefined;
}

function IntensityZoneBadge({ t, rowId, zone }: IntensityZoneBadgeProps) {
  if (zone === undefined) return <EmptyValue />;
  const position = zoneColorPosition(zone);
  return (
    <Badge
      variant="secondary"
      className={intensityBlockTextClass(position)}
      style={intensityColorStyle(position)}
      testId={setAdminProductsCellTestId(ADMIN_PRODUCTS_COLUMN_IDS.INTENSITY, rowId)}
    >
      {t(`admin.productCatalogue.intensityZones.${zone}`)}
    </Badge>
  );
}

function PositionCircle({ rowId, position }: { readonly rowId: string; readonly position: IntensityPosition | undefined }) {
  if (position === undefined) return <EmptyValue />;
  return (
    <span
      className={cn("inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium", intensityBlockTextClass(position))}
      style={intensityColorStyle(position)}
      data-testid={setAdminProductsCellTestId(ADMIN_PRODUCTS_COLUMN_IDS.POSITION, rowId)}
    >
      {position}
    </span>
  );
}

function BadgeList({ rowId, badges }: { readonly rowId: string; readonly badges: readonly string[] }) {
  if (badges.length === 0) return <EmptyValue />;
  return (
    <div className="flex flex-wrap gap-1">
      {badges.map((badge) => (
        <Badge key={badge} variant="secondary" testId={setAdminProductsCellTestId(ADMIN_PRODUCTS_COLUMN_IDS.BADGES, `${rowId}-${badge}`)}>
          {badge}
        </Badge>
      ))}
    </div>
  );
}

function WebStatusBadge({ t, rowId, status }: { readonly t: TFunction; readonly rowId: string; readonly status: WebStatus | undefined }) {
  if (!status) return <EmptyValue />;
  return (
    <Badge
      variant="secondary"
      className={WEB_STATUS_BADGE_CLASSES[status]}
      testId={setAdminProductsCellTestId(ADMIN_PRODUCTS_COLUMN_IDS.WEB_STATUS, rowId)}
    >
      {webStatusLabel(t, status)}
    </Badge>
  );
}

/** Width of the Product column; long coffee names wrap inside it. */
export const ADMIN_PRODUCTS_PRODUCT_COLUMN_SIZE = 320;

export const getAdminProductsColumns = (t: TFunction): ColumnDef<AdminCatalogueTableRow>[] => [
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.PRODUCT,
    accessorFn: (row) => row.title ?? "",
    size: ADMIN_PRODUCTS_PRODUCT_COLUMN_SIZE,
    header: t("admin.productCatalogue.columns.product"),
    cell: ({ row }) => (
      <div className="flex items-center gap-3" data-testid={setAdminProductsCellTestId(ADMIN_PRODUCTS_COLUMN_IDS.PRODUCT, row.original.id)}>
        <CatalogueThumbnail key={row.original.imageUrl} imageUrl={row.original.imageUrl} name={row.original.title ?? ""} />
        <div className="flex min-w-56 flex-col">
          {row.original.title ? <span className="text-sm font-medium">{row.original.title}</span> : <EmptyValue />}
          <span className="text-xs text-muted-foreground">{row.original.bagSize ?? "–"}</span>
        </div>
      </div>
    ),
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.ITEM_NUMBER,
    accessorFn: (row) => row.itemNumber ?? "",
    header: t("admin.productCatalogue.columns.itemNumber"),
    cell: ({ row }) => <span className="font-mono text-sm">{row.original.itemNumber ?? <EmptyValue />}</span>,
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.PARENT_ID,
    accessorFn: (row) => row.parentId ?? "",
    header: t("admin.productCatalogue.columns.parentId"),
    cell: ({ row }) => <span className="font-mono text-sm">{row.original.parentId ?? <EmptyValue />}</span>,
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.ROASTER,
    accessorFn: (row) => row.vendorName ?? "",
    header: t("admin.productCatalogue.columns.roaster"),
    cell: ({ row }) => (
      <div className="flex flex-col" data-testid={setAdminProductsCellTestId(ADMIN_PRODUCTS_COLUMN_IDS.ROASTER, row.original.id)}>
        {row.original.vendorName ? <span className="text-sm font-medium">{row.original.vendorName}</span> : <EmptyValue />}
        {row.original.vendorId ? (
          <span className="text-xs text-muted-foreground">
            {t("admin.productCatalogue.vendorIdLabel", { vendorId: row.original.vendorId })}
          </span>
        ) : null}
      </div>
    ),
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.INTENSITY,
    // Sorted by zone, lightest first; the zone is independent of the position.
    accessorFn: (row) => (row.intensityZone === undefined ? undefined : FLAVOUR_ZONES[row.intensityZone].min),
    sortUndefined: "last",
    header: t("admin.productCatalogue.columns.intensity"),
    cell: ({ row }) => (
      <AdminAttributeCell field="intensityZone" row={row.original}>
        <IntensityZoneBadge t={t} rowId={row.original.id} zone={row.original.intensityZone} />
      </AdminAttributeCell>
    ),
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.POSITION,
    accessorFn: (row) => row.intensityPosition,
    sortUndefined: "last",
    header: t("admin.productCatalogue.columns.position"),
    cell: ({ row }) => (
      <AdminAttributeCell field="intensityPosition" row={row.original}>
        <PositionCircle rowId={row.original.id} position={row.original.intensityPosition} />
      </AdminAttributeCell>
    ),
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.CHARACTER,
    accessorFn: (row) => row.character ?? "",
    header: t("admin.productCatalogue.columns.character"),
    cell: ({ row }) => (
      <AdminAttributeCell field="character" row={row.original}>
        {textOrDash(row.original.character)}
      </AdminAttributeCell>
    ),
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.BADGES,
    accessorFn: (row) => row.badges.join(", "),
    filterFn: (row, _columnId, value) => matchesBadgeSelection(row.original.badges, toFilterSelection(value)),
    header: t("admin.productCatalogue.columns.badges"),
    cell: ({ row }) => (
      <AdminAttributeCell field="badges" row={row.original}>
        <BadgeList rowId={row.original.id} badges={row.original.badges} />
      </AdminAttributeCell>
    ),
  },
  {
    id: ADMIN_PRODUCTS_COLUMN_IDS.WEB_STATUS,
    accessorFn: (row) => row.webStatus ?? "",
    filterFn: (row, _columnId, value) => matchesStatusSelection(row.original.webStatus, toFilterSelection(value)),
    header: t("admin.productCatalogue.columns.webStatus"),
    cell: ({ row }) => (
      <AdminWebStatusCell row={row.original}>
        <WebStatusBadge t={t} rowId={row.original.id} status={row.original.webStatus} />
      </AdminWebStatusCell>
    ),
  },
];
